import { ReactComponent as IMG_DOWNLOAD_ICON } from '~/assets/images/download-button/download-icon.svg';
import { ReactComponent as IMG_HIGHLIGHT_LEFT_TOP } from '~/assets/images/download-button/highlight-left-top.svg';

import styled from '@emotion/styled';
import { rem } from 'polished';
import React, { MouseEventHandler, useCallback } from 'react';

import { css, scale } from '~/css';
import cn from '~/shared/utils/cn';
import { useTexts } from '~/shared/libs/gatsby-plugin-i18n';

interface DownloadButtonProps {
  id?: string;
  className?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

const Container = styled('button')(
  css({
    cursor: 'pointer',
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f2f2f2',
    width: [scale(200 * 0.9), rem(200 * 0.8), 180],
    height: [scale(50 * 0.9), rem(50 * 0.8), 50],
    fontSize: [scale(28 * 0.9), rem(28 * 0.8), 28],
    letterSpacing: '-0.05em',
    borderRadius: [scale(8 * 0.9), rem(8 * 0.9), 8],
    ':lang(en)': {
      fontSize: [scale(26 * 0.8), rem(26 * 0.8), 26],
    },
    ':lang(ja)': {
      fontSize: [scale(24 * 0.8), rem(24 * 0.8), 24],
    },
  }),
);

const DownloadIcon = styled(IMG_DOWNLOAD_ICON)(
  css({
    width: [scale(28 * 0.9), rem(28 * 0.8), 29],
    height: 'auto',
    marginRight: [scale(15 * 0.9), rem(15 * 0.8), 15],
  }),
);

const Highlight = styled(IMG_HIGHLIGHT_LEFT_TOP)(
  css({
    position: 'absolute',
    top: [scale(1 * 0.9), rem(1 * 0.8), 1],
    left: [scale(2 * 0.9), rem(2 * 0.8), 2],
    width: [scale(12.55 * 0.9), rem(12.55 * 0.8), rem(12.55)],
    height: [scale(11.56 * 0.9), rem(11.56 * 0.8), rem(11.56)],
  }),
);

const DownloadButton: React.FC<DownloadButtonProps> = ({ id, className, onClick }) => {
  const t = useTexts();
  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      if (onClick) {
        onClick(e);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        e.currentTarget.blur();
      }
    },
    [onClick],
  );

  return (
    <Container id={id} className={cn('font-cookierun-bold', className)} onClick={handleClick}>
      <Highlight viewBox='0 0 12.55 11.56' />
      <DownloadIcon viewBox='0 0 29 27' />
      {t`download`}
    </Container>
  );
};

export default DownloadButton;
